import React, {FC, useEffect, useState} from "react"
import CopyToClipboard from "react-copy-to-clipboard"
import styled from "styled-components"

import GenericModal from "@irati/ui/src/GenericModal"
import {Body, H2, H4} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import Button from "@irati/ui/src/Button"
import wellnessService from "@irati/wellness/services/Wellness"
import {getDomain} from "@irati/utils/links"
import authentication from "@irati/auth"

import Loading from "@irati/ui/src/Loading"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {WellnessMedia} from "@irati/types"
import MediaCard from "@irati-web/wellness/src/components/MediaCard"
import {adminDashboardTracking} from "@irati-tracking/admin"
import ModalWidget from "../ModalWidget"

const WellnessMediaWrapper = styled.div`
  width: 380px;
  margin: 0 auto;
`

interface Props extends InjectIntl {
  show: boolean
  onHide: () => void
}

const MOST_POPULAR_CONTENT_ID = "1"

const WellnessModal: FC<Props> = ({show, onHide, formatMessage}) => {
  const [isCopied, setIsCopied] = useState(false)
  const [showPromote, setShowPromote] = useState<boolean>(false)
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [mostPopularWellnessContent, setMostPopularWellnessContent] = useState<WellnessMedia[]>([])

  useEffect(() => {
    const wellnessLoad = wellnessService.getLanes().then((res) => {
      const mostPopularVideos = res.find(({id}) => id === MOST_POPULAR_CONTENT_ID)?.mediaList
      if (mostPopularVideos) {
        setMostPopularWellnessContent(mostPopularVideos)
      }
    })
    setLoad(wellnessLoad)
  }, [])

  const getWellnessCopyText = () => {
    return `${formatMessage({
      id: "admin.components.dashboard.reporting.wellness.modal.copyToClipboard.top"
    })}\
    ${mostPopularWellnessContent
      .map(
        (content, i) =>
          `\n${formatMessage(
            {id: "admin.components.dashboard.reporting.wellness.modal.copyToClipboard.content"},
            {
              position: i + 1,
              title: content.title,
              category: content.category.name,
              link: `${getDomain(authentication.business().subdomain)}/wellness/details/${content.uuid}`
            }
          )}`
      )
      .join(`\n`)}
    `
  }

  const handleHide = () => {
    setShowPromote(false)
    onHide()
  }

  const handleCopyClick = () => {
    adminDashboardTracking.wellnessActions("cta_wellnessText")
    setIsCopied(true)
  }

  const handlePromoteClick = () => {
    adminDashboardTracking.wellnessActions("cta_shareWellness")
    setShowPromote(true)
  }

  const copyText = isCopied
    ? "admin.components.dashboard.reporting.costOfLiving.modal.copiedToClipboard"
    : "admin.components.dashboard.reporting.costOfLiving.modal.copyButton"

  return (
    <GenericModal hideActions show={show} customWidth={884} onHide={handleHide}>
      {showPromote ? (
        <div>
          <H2 marginBottom={2} maxWidth="100%">
            <FormattedMessage id="admin.components.dashboard.reporting.wellness.modal.topVideos.title" />
          </H2>
          <Body marginBottom={2} maxWidth="100%">
            <FormattedMessage id="admin.components.dashboard.reporting.wellness.modal.topVideos.body" />
          </Body>
          <Body textAlign="center" maxWidth="100%" marginBottom={2}>
            <FormattedMessage id="admin.components.dashboard.reporting.wellness.modal.topVideos.text" />
          </Body>
          <H4 textAlign="center" maxWidth="100%" marginBottom={2}>
            <FormattedMessage id="admin.components.dashboard.reporting.wellness.modal.topVideos.subtitle" />
          </H4>
          <Loading load={load}>
            {mostPopularWellnessContent.slice(0, 3).map((content) => {
              return (
                <div key={content.id}>
                  <WellnessMediaWrapper>
                    <MediaCard media={content} key={content.id} />
                  </WellnessMediaWrapper>
                  <Body textAlign="center" marginTop={2} maxWidth="100%">
                    {content.title}
                  </Body>
                  <Body small textAlign="center" marginBottom={2} maxWidth="100%">
                    {content.category.name}
                  </Body>
                </div>
              )
            })}
            {mostPopularWellnessContent?.length === 0 && (
              <Body>
                <FormattedMessage id="admin.components.dashboard.reporting.wellness.modal.empty" />
              </Body>
            )}
          </Loading>
          {isCopied && (
            <Alert
              type={AlertTypes.success}
              title="admin.components.dashboard.reporting.wellness.modal.alert"
              onHide={() => setIsCopied(false)}
            />
          )}
          <Flex justifyContent="space-between" alignItems="center">
            <Button tertiary onClick={() => setShowPromote(false)}>
              <Icon icon={IconTypes.chevronLeft} marginRight />
              <FormattedMessage id="platform.button.back" />
            </Button>
            <CopyToClipboard text={getWellnessCopyText()}>
              <Button onClick={handleCopyClick}>
                <FormattedMessage id={copyText} />
              </Button>
            </CopyToClipboard>
          </Flex>
        </div>
      ) : (
        <>
          <H2 marginBottom={1}>
            <FormattedMessage id="admin.components.dashboard.reporting.wellness.modal.widgets.title" />
          </H2>
          <Body marginBottom={2} maxWidth="100%">
            <FormattedMessage id="admin.components.dashboard.reporting.wellness.modal.widgets.text" />
          </Body>
          <Flex gap={1}>
            <ModalWidget
              title="admin.components.dashboard.reporting.wellness.modal.widgets.promote.title"
              buttonText="admin.components.dashboard.reporting.wellness.modal.widgets.promote.button"
              emoji="🍵"
              onClick={handlePromoteClick}
              text="admin.components.dashboard.reporting.wellness.modal.widgets.promote.text"
            />
            <ModalWidget
              title="admin.components.dashboard.reporting.wellness.modal.widgets.challenge.title"
              buttonText="admin.components.dashboard.reporting.wellness.modal.widgets.challenge.button"
              emoji="🏋️‍♂️"
              text="admin.components.dashboard.reporting.wellness.modal.widgets.challenge.text"
              href="admin.components.dashboard.reporting.wellness.modal.widgets.challenge.href"
              onClick={() => adminDashboardTracking.wellnessActions("cta_marketingResource")}
              isRecommended
            />
          </Flex>
        </>
      )}
    </GenericModal>
  )
}

export default injectIntl(WellnessModal)
